import type { AccountInfo } from "@azure/msal-browser";
import LogoutIcon from "@mui/icons-material/Logout";
import LoginIcon from "@mui/icons-material/PersonAddAlt";
import InactiveAccountIcon from "@mui/icons-material/PersonOutlined";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import ListItemIcon from "@mui/material/ListItemIcon";
import MenuItem from "@mui/material/MenuItem";
import { useCallback } from "react";

import { useAuth } from "~auth/useAuth.ts";
import { useTranslation } from "~contexts/I18nContext/I18nContext.tsx";
import { useOfflineContext } from "~contexts/OfflineContext/useOfflineContext.tsx";

import { HeaderMenu } from "../HeaderMenu.tsx";
import { HeaderMenuIconButton } from "../HeaderMenuIconButton.tsx";

import styles from "./UserMenu.module.scss";

interface UserMenuContentProps {
  closeMenu: () => void;
}

function UserMenuContent({ closeMenu }: UserMenuContentProps) {
  const t = useTranslation();
  const { isOffline } = useOfflineContext();

  const {
    switchUser,
    activeAccount,
    inactiveAccounts,
    loginAdditionalUser,
    logout,
  } = useAuth();

  const select = useCallback(
    async (account: AccountInfo) => {
      switchUser(account);
      closeMenu();
    },
    [switchUser, closeMenu],
  );

  return (
    <div>
      <div className={styles.activeAccount} data-testid="active-account">
        <div className={styles.activeAccount__header}>
          <p className={styles.activeAccount__label}>{t.loggedInAs}</p>
          <Button
            onClick={logout}
            size="small"
            startIcon={<LogoutIcon />}
            className={styles.activeAccount__logout}
            disabled={isOffline}
          >
            {t.signOut}
          </Button>
        </div>
        <p className={styles.activeAccount__name}>{activeAccount?.name}</p>
        <p className={styles.activeAccount__email}>{activeAccount?.username}</p>
      </div>

      <div className={styles.inactiveAccounts}>
        {inactiveAccounts.map((a) => (
          <MenuItem
            key={a.username}
            onClick={() => select(a)}
            className={styles.inactiveAccount}
            disabled={isOffline}
          >
            <ListItemIcon>
              <InactiveAccountIcon />
            </ListItemIcon>
            <div>
              <p className={styles.inactiveAccount__name}>{a.name}</p>
              <p className={styles.inactiveAccount__email}>{a.username}</p>
            </div>
          </MenuItem>
        ))}
      </div>
      <MenuItem onClick={loginAdditionalUser} disabled={isOffline}>
        <ListItemIcon>
          <LoginIcon />
        </ListItemIcon>
        {t.loginAnotherUser}
      </MenuItem>
    </div>
  );
}

export interface UserMenuProps {
  userName?: string;
}

export function UserMenu({ userName }: UserMenuProps) {
  return (
    <HeaderMenu
      data-testid="user-menu"
      component={(handleClick) => (
        <HeaderMenuIconButton
          onClick={handleClick}
          className={styles.avatarButton}
          icon={
            <Avatar className={styles.avatarButton__avatar} alt={userName}>
              {userName?.charAt(0)}
            </Avatar>
          }
        />
      )}
    >
      {(closeMenu) => <UserMenuContent closeMenu={closeMenu} />}
    </HeaderMenu>
  );
}
